import React, { Fragment, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import Image from 'next/image'
import { useRecoilState } from 'recoil'
import BackIcon from '../../../../public/static/arrow_left.svg'
import {
  InGameAccountModalState,
  isNoAccountsState,
  isOpenInGameAccountModalState,
  selectCategoryProfileState,
} from '../../../../common/atoms/checkout'
import { InGameAccountModalStatusEnum } from '../../../../common/enums/in-game-account-modal-status.enum'
import { CategoryProfileResponse } from '../../../../types/users/category-profile-response'
import AccountsSection from './Section/AccountsSection'
import { useTranslation } from 'next-i18next'
import { selectedGameState } from '../../../../common/atoms/atoms'
import EditInGameAccountSection from '../../../home/EditInGameAccountSection'
import ModalCloseButton from '../../../common/modals/ModalCloseButton'
import { useSession } from 'next-auth/react'
import { SessionStatusEnum } from '../../../../common/enums/session-status.enum'

type Props = {
  profiles?: CategoryProfileResponse[]
  getProfiles: any
}

export default function InGameAccountModal({ profiles, getProfiles }: Props) {
  const [isOpen] = useRecoilState(isOpenInGameAccountModalState)
  const [modalStatus, setModalStatus] = useRecoilState(InGameAccountModalState)
  const [, setChosenId] = useRecoilState(selectCategoryProfileState)
  const [, setIsNoAccounts] = useRecoilState(isNoAccountsState)
  const [game] = useRecoilState(selectedGameState)
  const { t } = useTranslation('inGameModal')
  const { status } = useSession()
  useEffect(() => {
    if (isOpen) {
      // @ts-ignore,
      if (status === SessionStatusEnum.UNAUTHENTICATED) {
        setChosenId(-1)
        setIsNoAccounts(true)
        setModalStatus(InGameAccountModalStatusEnum.GUEST)
      } else if (profiles && profiles.length > 0) {
        setIsNoAccounts(false)
        setModalStatus(InGameAccountModalStatusEnum.ACCOUNTS)
      } else {
        setChosenId(-1)
        setIsNoAccounts(true)
        setModalStatus(InGameAccountModalStatusEnum.NO_ACCOUNT)
      }
    }
  }, [isOpen, status])

  return (
    <div>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as='div' className='relative z-30' onClose={() => {}}>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='modal-mask' />
          </Transition.Child>

          <div className='fixed inset-0 overflow-y-auto'>
            <div className='flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0'>
              <Transition.Child
                as={Fragment}
                enter='ease-out duration-300'
                enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
                enterTo='opacity-100 translate-y-0 sm:scale-100'
                leave='ease-in duration-200'
                leaveFrom='opacity-100 translate-y-0 sm:scale-100'
                leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              >
                <Dialog.Panel className='modal-dialog'>
                  {(modalStatus === InGameAccountModalStatusEnum.NO_ACCOUNT ||
                    modalStatus === InGameAccountModalStatusEnum.ADD) && (
                    <ModalCloseButton
                      isOpenState={isOpenInGameAccountModalState}
                    />
                  )}

                  {modalStatus === InGameAccountModalStatusEnum.EDIT && (
                    <div className='absolute left-0 top-0 pl-5 pt-5'>
                      <button
                        type='button'
                        className='rounded-md text-gray-400 hover:text-gray-500 focus:outline-none'
                        onClick={(e) => {
                          e.preventDefault()
                          setModalStatus(InGameAccountModalStatusEnum.ACCOUNTS)
                        }}
                      >
                        <Image
                          src={BackIcon}
                          width={32}
                          height={32}
                          alt={'Back icon'}
                        />
                      </button>
                    </div>
                  )}

                  {modalStatus === InGameAccountModalStatusEnum.EDIT && (
                    <ModalCloseButton
                      isOpenState={isOpenInGameAccountModalState}
                    />
                  )}

                  {(modalStatus === InGameAccountModalStatusEnum.ACCOUNTS ||
                    modalStatus === InGameAccountModalStatusEnum.GUEST) && (
                    <ModalCloseButton
                      isOpenState={isOpenInGameAccountModalState}
                    />
                  )}

                  <div className={'flex flex-col items-center'}>
                    {(modalStatus === InGameAccountModalStatusEnum.NO_ACCOUNT ||
                      modalStatus === InGameAccountModalStatusEnum.ADD) && (
                      <h6 className={'modal-title'}>
                        {t('addAccountModalTitle')}
                      </h6>
                    )}
                    {modalStatus === InGameAccountModalStatusEnum.ACCOUNTS && (
                      <h6 className={'modal-title'}>
                        {t('accountsModalTitle')}
                      </h6>
                    )}
                    {modalStatus === InGameAccountModalStatusEnum.EDIT && (
                      <h6 className={'modal-title'}>{t('editModalTitle')}</h6>
                    )}
                    {modalStatus === InGameAccountModalStatusEnum.GUEST && (
                      <h6 className={'modal-title'}>{t('guestModalTitle')}</h6>
                    )}
                  </div>

                  {/*Accounts*/}
                  {modalStatus === InGameAccountModalStatusEnum.ACCOUNTS && (
                    <AccountsSection
                      game={game}
                      profiles={profiles}
                      getProfile={getProfiles}
                    />
                  )}

                  {/*No Account*/}
                  {modalStatus === InGameAccountModalStatusEnum.NO_ACCOUNT && (
                    <EditInGameAccountSection
                      game={game}
                      profiles={profiles}
                      getProfiles={getProfiles}
                      type={'adding'}
                    />
                  )}

                  {/*Edit*/}
                  {modalStatus === InGameAccountModalStatusEnum.EDIT && (
                    <EditInGameAccountSection
                      profiles={profiles}
                      getProfiles={getProfiles}
                      game={game}
                      type={'editing'}
                    />
                  )}

                  {(modalStatus === InGameAccountModalStatusEnum.ADD ||
                    modalStatus === InGameAccountModalStatusEnum.GUEST) && (
                    <EditInGameAccountSection
                      profiles={profiles}
                      getProfiles={getProfiles}
                      game={game}
                      type={`${
                        modalStatus === InGameAccountModalStatusEnum.GUEST
                          ? 'guest'
                          : 'adding'
                      }`}
                    />
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  )
}
